import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import styles from '../../../styles/containers/Lightbox.scss';
import { connect } from 'react-redux';
import { pick, times } from 'lodash';
import PdfWrapperHeader from './PdfWrapperHeader';
import { updateAttachmentVersionAnnotation } from '../../../redux/actions/general/lightbox';
import { loadPdf } from './pdfLoader';
import PdfPage from './PdfPage';
import { VariableSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { getRowHeight, getPageScrollMetrics } from './utils/pdfScrollMath';
import { usePdfScrollSync } from './hooks/usePdfScrollSync';
import {
  clearAllCanvases,
  updateOverlay,
  updateTool,
} from '../../../redux/actions/canvas_editor';
import PdfToolbar from '../../../components/CanvasEditor/components/Toolbar/PdfToolbar';
import { normalizeAnnotationsForBackend } from './utils/pdfFabricUtils';

import {
  resetObjectStyle,
  setThicknessContext,
  selectObjectStyle,
} from '../../../components/CanvasEditor/helpers/fabricHelpers';

import { FABRIC_TO_PDF_MAPPERS } from './pdfExport/mappers';
import { exportPdfWithAnnotations } from './pdfExport/exportPdfWithAnnotations';

const PAGE_GAP = 10;

const PdfWrapper = ({
  file,
  pdfScale,
  annotations,
  user_full_name,
  disabled,
}) => {
  const [pdfDoc, setPdfDoc] = useState(null);
  const containerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);

  const listRef = useRef(null);

  const pageBaseDimensionsRef = useRef([]);
  const documentBaseScaleRef = useRef(null);
  const fallbackDimensionsRef = useRef(null);
  const fabricRegistryRef = useRef({});

  const gap = pdfDoc?.numPages > 1 ? PAGE_GAP : 0;

  function saveAllAnnotations(pageDimensions) {
    if (!pageDimensions?.length) return;

    let allAnnotations = [];

    Object.entries(fabricRegistryRef.current).forEach(
      ([pageNumber, canvas]) => {
        if (!canvas) return;

        // Reset active selection da bi mogli pravilno uzeti objekte
        const activeObject = canvas.getActiveObject();
        if (activeObject?.type === 'activeSelection') {
          activeObject.getObjects().forEach((child) => child.setCoords());
          canvas.discardActiveObject();
        }

        const pageIndex = Number(pageNumber) - 1;
        const pageDimensionsForCanvas = pageDimensions[pageIndex];

        activeObject && resetObjectStyle(activeObject);

        const canvasWidth = canvas.getWidth();
        const canvasHeight = canvas.getHeight();

        const normalizedAnnotations = normalizeAnnotationsForBackend(
          canvas,
          canvasWidth,
          canvasHeight,
          canvasWidth,
          canvasHeight
        );

        if (normalizedAnnotations.length > 0) {
          allAnnotations.push(...normalizedAnnotations);
        }

        activeObject && selectObjectStyle(activeObject);
      }
    );

    const serializedObjects = JSON.stringify({ objects: allAnnotations });

    if (file.attachment_version_id) {
      updateAttachmentVersionAnnotation(file.attachment_version_id, serializedObjects);
    }
  }

  /**
   * Load PDF and calculate base scale so first page fits container width
   */
  useEffect(() => {
    if (!file?.download_url) return;

    let cancelled = false;

    setPdfDoc(null);
    pageBaseDimensionsRef.current = [];
    documentBaseScaleRef.current = null;
    fallbackDimensionsRef.current = null;
    // setPageDimensionsVersion(0);
    setIsLoading(true);
    !disabled && setThicknessContext({ mode: 'pdf' });

    loadPdf(file.download_url)
      .then((pdf) => {
        if (cancelled) return;

        return pdf.getPage(1).then((page) => {
          if (cancelled) return;

          const viewport = page.getViewport({ scale: 1 });
          const viewportWidth = viewport.width;
          const fitScale =
            ((containerRef.current?.clientWidth || viewportWidth) /
              viewportWidth) *
            0.9;
          const firstPageDimensions = {
            width: viewport.width * fitScale,
            height: viewport.height * fitScale,
            fitScale,
          };

          const initialDimensions = times(pdf.numPages, () => null);
          initialDimensions[0] = firstPageDimensions;

          pageBaseDimensionsRef.current = initialDimensions;
          documentBaseScaleRef.current = fitScale;
          fallbackDimensionsRef.current = pick(firstPageDimensions, [
            'width',
            'height',
          ]);
          // setPageDimensionsVersion((version) => version + 1);

          !disabled && updateOverlay({ fabricOverlay: null, viewer: null });
          setPdfDoc(pdf);
          !disabled && updateTool({ tool: 'hand' });
          setIsLoading(false);
        });
      })
      .catch((err) => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;

      if (!disabled) {
        saveAllAnnotations(pageBaseDimensionsRef.current);
        clearAllCanvases();
      }

      Object.values(fabricRegistryRef.current).forEach((canvas) => {
        canvas?.dispose();
      });

      fabricRegistryRef.current = {};
    };
  }, [file?.download_url, disabled]);

  const normalizedAnnotations = useMemo(() => {
    const byPage = {};

    (annotations?.objects || []).forEach((obj) => {
      const page = obj.pageNumber ?? 1;

      if (!byPage[page]) {
        byPage[page] = [];
      }

      byPage[page].push(obj);
    });

    return byPage;
  }, [annotations]);

  const annotationsReady = Array.isArray(annotations?.objects);

  const onPageBaseDimensions = useCallback((pageNumber, dimensions) => {
    if (!pageNumber || !dimensions) return;

    const pageIndex = pageNumber - 1;
    const pageDimensions = pageBaseDimensionsRef.current;
    const previousDimensions = pageDimensions[pageIndex];

    if (
      previousDimensions &&
      previousDimensions.width === dimensions.width &&
      previousDimensions.height === dimensions.height &&
      previousDimensions.fitScale === dimensions.fitScale
    ) {
      return;
    }

    pageDimensions[pageIndex] = dimensions;

    if (!documentBaseScaleRef.current && dimensions.fitScale) {
      documentBaseScaleRef.current = dimensions.fitScale;
    }

    if (!fallbackDimensionsRef.current) {
      fallbackDimensionsRef.current = {
        width: dimensions.width,
        height: dimensions.height,
      };
    }

    listRef.current?.resetAfterIndex?.(pageIndex, false);
    // setPageDimensionsVersion((version) => version + 1);
  }, []);

  const getScrollMetrics = useCallback(() => {
    return getPageScrollMetrics({
      pageDimensions: pageBaseDimensionsRef.current,
      fallbackDimensions: fallbackDimensionsRef.current,
    });
  }, []);

  const getDocumentBaseScale = useCallback(() => {
    return documentBaseScaleRef.current;
  }, []);

  const handleExportPdfWithAnnotations = useCallback(() => {
    return exportPdfWithAnnotations({
      downloadUrl: file?.download_url,
      fileName: file?.name,
      fabricRegistry: fabricRegistryRef.current,
      userFullName: user_full_name,
      mappers: FABRIC_TO_PDF_MAPPERS,
    }).catch((err) => {
      console.error('Failed to export PDF with annotations:', err);
    });
  }, [file?.download_url, file?.name, user_full_name]);

  /**
   * Keep visual position stable when zoom changes
   */
  usePdfScrollSync({
    listRef,
    pdfScale,
    getScrollMetrics,
    gap,
  });

  /**
   * Row height used by react-window
   */
  const itemHeight = useCallback(
    (index) =>
      getRowHeight({
        pageHeight:
          pageBaseDimensionsRef.current[index]?.height ||
          fallbackDimensionsRef.current?.height ||
          0,
        scale: pdfScale,
        gap,
      }),
    [pdfScale, gap]
  );

  /**
   * Single PDF page row
   */
  const Row = useCallback(
    ({ index, style }) => {
      return (
        <div style={style}>
          <PdfPage
            disabled={disabled}
            initialAnnotationsByPage={normalizedAnnotations[index + 1] || []}
            fabricRegistryRef={fabricRegistryRef}
            gap={gap}
            pdfDoc={pdfDoc}
            pageNumber={index + 1}
            totalPages={pdfDoc.numPages}
            getDocumentBaseScale={getDocumentBaseScale}
            onPageBaseDimensions={onPageBaseDimensions}
          />
        </div>
      );
    },
    [
      normalizedAnnotations,
      gap,
      pdfDoc,
      getDocumentBaseScale,
      onPageBaseDimensions,
      disabled,
    ]
  );

  // /**
  //  * Fired on every scroll event from react-window List
  //  * Used to calculate which PDF page is currently visible
  //  */
  // const onScroll = ({ scrollOffset }) => {
  //   const page = getCurrentPageFromScroll({
  //     scrollTop: scrollOffset,
  //     pageHeight: basePageDimensions?.height,
  //     scale: pdfScale,
  //     hasGap,
  //   });

  //   setCurrentPage(page);
  // };

  // /**
  //  * Scrolls the virtualized list to a specific PDF page
  //  * Used when user types a page number or clicks pagination controls
  //  */
  // const jumpToPage = (pageNumber) => {
  //   if (!listRef.current) return;

  //   const scrollTop = getScrollForPage({
  //     pageIndex: pageNumber - 1,
  //     basePageHeight,
  //     baseScale,
  //     scale: pdfScale,
  //     hasGap,
  //   });

  //   listRef.current.scrollTo(scrollTop);
  // };

  return (
    <>
      <PdfWrapperHeader
        file={file}
        onExportPdfWithAnnotations={handleExportPdfWithAnnotations}
      />

      <div className={styles.pdfAnnotator} ref={containerRef}>
        {(isLoading || !annotationsReady) && (
          <div className={styles.loader}>
            <div className={styles.fadeIn}>
              <div className={styles.spinner} />
              <div>Loading PDF...</div>
            </div>
          </div>
        )}
        {pdfDoc && annotationsReady && (
          <>
            <AutoSizer>
              {({ height, width }) => (
                <List
                  ref={listRef}
                  height={height}
                  width={width}
                  itemCount={pdfDoc.numPages}
                  overscanCount={2}
                  itemSize={itemHeight}
                  itemKey={(index) => {
                    return index;
                  }}
                  //onScroll={onScroll}
                >
                  {Row}
                </List>
              )}
            </AutoSizer>

            {!disabled && <PdfToolbar sourceType='pdf' />}
          </>
        )}
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  pdfScale: state.general.lightbox.pdfScale,
  source: state.canvas_editor.source,
  activePage: state.canvas_editor.activePage,
});

export default connect(mapStateToProps, null)(PdfWrapper);
